import React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import autobind from 'core-decorators/lib/autobind';

import PropTypes from 'prop-types';

import attachScript from '@wix/wix-vod-shared/src/common/utils/attach-script';
import URL from 'shared/utils/url';

import { isFBPlayerHackEnabled } from 'shared/selectors/experiments';

const scriptApiUrl = 'https://connect.facebook.net/en_US/sdk.js';
let scriptPromise = null;

const iframeUrlWithDimensions = (src, { width, height }) => {
  const url = new URL(src);
  url.addParam('height', Math.floor(height));
  url.addParam('width', Math.floor(width));
  return url.toString();
};

@connect(state => ({
  isFBPlayerHackEnabled: isFBPlayerHackEnabled(state),
}))
export default class FacebookExternal extends React.Component {
  static propTypes = {
    src: PropTypes.string.isRequired,

    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    onEnd: PropTypes.func,
    onIframeRef: PropTypes.func.isRequired,

    width: PropTypes.number,
    height: PropTypes.number,
    className: PropTypes.string,
    videoItem: PropTypes.object,

    simple: PropTypes.bool,
    mobileMode: PropTypes.bool,

    isVideoPlayRequested: PropTypes.bool,
    isVideoPauseRequested: PropTypes.bool,
    isVideoPlaying: PropTypes.bool,
    isVideoPaused: PropTypes.bool,

    loop: PropTypes.bool,
    muted: PropTypes.bool,

    isFBPlayerHackEnabled: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.player = null;
    this.containerRef = null;
    this.iframeRef = null;
    this.iframeSrcWasHacked = false;

    this.state = {
      isReady: false,
    };
  }

  componentDidMount() {
    window.fbAsyncInit = this.fbAsyncInit;

    // load once
    if (this.isScriptLoaded) {
      this.fbAsyncInit();
    } else if (!scriptPromise) {
      scriptPromise = attachScript(scriptApiUrl);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.src === this.props.src) {
      this.setSettings(nextProps, this.props);
    } else {
      this.setState({ isReady: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.isPropsChanged(prevProps, ['src', 'width', 'height']) && this.isScriptLoaded) {
      this.fbAsyncInit();
    }

    if (this.isPropsChanged(prevProps, ['isVideoPauseRequested', 'isVideoPlayRequested'])) {
      this.updateFocusAbility();
    }
  }

  componentWillUnmount() {
    window.fbAsyncInit = null;
    if (this.isScriptLoaded) {
      window.FB.Event.unsubscribe('xfbml.ready', this.handleFacebookReady);
    }
    if (this.player) {
      this.detachEventsFromPlayer();
    }
  }

  isPropsChanged(prevProps, propNames) {
    const prev = _.pick(prevProps, propNames);
    const current = _.pick(this.props, propNames);

    return !_.isEqual(prev, current);
  }

  updateFocusAbility() {
    if (!this.iframeRef) {
      return;
    }

    const { isVideoPauseRequested, isVideoPlayRequested, isVideoPlaying, isVideoPaused } = this.props;
    const tabIndex = isVideoPauseRequested || isVideoPlayRequested || isVideoPlaying || isVideoPaused ? 0 : -1;
    this.iframeRef.setAttribute('tabindex', tabIndex);
  }

  @autobind
  saveContainerRef(ref) {
    this.containerRef = ref;
  }

  attachEventsToPlayer() {
    const { onPlay, onPause, onEnd } = this.props;

    this._eventHanlers = {};
    this._eventHanlers.onPlay = this.player.subscribe('startedPlaying', onPlay);
    this._eventHanlers.onPause = this.player.subscribe('paused', onPause);
    this._eventHanlers.onEnd = this.player.subscribe('finishedPlaying', onEnd);
  }

  detachEventsFromPlayer() {
    this._eventHanlers.onPlay.release();
    this._eventHanlers.onPause.release();
    this._eventHanlers.onEnd.release();
    this._eventHanlers = {};
  }

  didSubscribe = false;

  get isScriptLoaded() {
    return _.has(window, 'FB');
  }

  @autobind
  fbAsyncInit() {
    window.FB.init({
      xfbml: true,
      version: 'v2.5',
    });

    if (this.didSubscribe) {
      return;
    }

    if (window.FB) {
      window.FB.Event.subscribe('xfbml.ready', this.handleFacebookReady);
    }
    this.didSubscribe = true;
  }

  @autobind
  handleFacebookReady(message) {
    if (message.type === 'video') {
      this.onReady(message.instance);
    }
  }

  @autobind
  hackIframeDimensions(iframe) {
    iframe.style.height = `${Math.floor(this.props.height)}px`;
    iframe.style.width = `${Math.floor(this.props.width)}px`;
  }

  @autobind
  hackIframeSrc(iframe) {
    const { width, height, videoItem } = this.props;
    if (
      // to prevent infinite loop since fb's 'xfbml.ready' will be fired after iframe reload
      this.iframeSrcWasHacked ||
      // means fb video dimensions are "less portrait" then widget viewport so no need for this shit
      (videoItem.height / videoItem.width) <= (height / width)
      ) {
        return;
      }

    iframe.src = iframeUrlWithDimensions(iframe.src, { width, height });
    this.iframeSrcWasHacked = true;
  }

  @autobind
  onReady(player) {
    this.player = player;

    this.iframeRef = this.containerRef.querySelector('iframe');
    this.props.onIframeRef(this.iframeRef);
    if (this.props.isFBPlayerHackEnabled) {
      this.iframeRef.onload = () => this.hackIframeDimensions(this.iframeRef);
      this.hackIframeSrc(this.iframeRef);
    }

    this.attachEventsToPlayer();
    this.setState({ isReady: true });
    this.setSettings(this.props);
    this.updateFocusAbility();
  }

  setSettings({ isVideoPlayRequested, isVideoPauseRequested, muted }, prevProps = {}) {
    if (!this.player || !this.state.isReady) {
      return null;
    }

    if (prevProps.isVideoPlayRequested !== isVideoPlayRequested && isVideoPlayRequested) {
      this.togglePaused(false);
    }

    if (prevProps.isVideoPauseRequested !== isVideoPauseRequested && isVideoPauseRequested) {
      this.togglePaused(true);
    }

    this.toggleMute(muted);
  }

  togglePaused(paused) {
    if (paused) {
      this.player.pause();
    } else {
      this.player.play();
    }
  }

  toggleMute(muted) {
    if (muted) {
      this.player.mute();
    } else {
      this.player.unmute();
    }
  }

  render() {
    const { src, className, width, height, isFBPlayerHackEnabled } = this.props;

    let props = {
      className: classnames('fb-video', className),
      ref: this.saveContainerRef,
      'data-href': src,
      'data-allowfullscreen': 'true',
      'data-width': Math.ceil(width),
      'data-height': Math.ceil(height),
    };

    if (isFBPlayerHackEnabled) {
      props = _.omit(props, ['data-width', 'data-height']);
    }

    return (
      <div {...props} />
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/player-widget/components/new-player/external/facebook.js